import React, { Component, ReactNode } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import omit from 'lodash/omit'
import forOwn from 'lodash/forOwn'
import { History } from 'history'

import { fetchVehicles, fetchMakes } from './vehiclesActions'
import {
  nextPage,
  goToPage,
  previousPage,
} from '../../helpers/paginationHelper'
import { selectVehicleMeta } from '../../reducers/vehicles'
import { selectVehicleIndex } from '../../reducers/vehicles'
import {
  parseQuery,
  stringifyQuery,
  convertStringToBoolean,
} from '../../helpers/queryHelper'
import { redirectToSingleResult } from '../../helpers/indexPageHelper'
import { displayNotification } from '../../actions/notificationsActions'
import { Vehicle } from '../../core/types'

import TablePagination from '../../components/tablePagination'

export type Callback = (...args: any[]) => void

interface Meta {
  total: number
}

export interface Props {
  fetchVehicles: Callback
  displayNotification: Callback
  currentVehicles: Vehicle[]
  location: { key: any } & Location
  history: History
  meta: Meta
}

export class VehiclesIndex extends Component<Props> {
  static displayName = 'VehicleIndex'

  componentWillMount() {
    const { fetchVehicles, location, history } = this.props
    if (!location.search) {
      history.push({
        pathname: '/vehicles',
        search: stringifyQuery({ offset: 0, limit: 25 }),
      })
    } else {
      const parsedQuery = convertStringToBoolean(parseQuery(location.search))
      fetchVehicles(parsedQuery)
    }
  }

  componentDidUpdate(prevProps) {
    const {
      fetchVehicles,
      currentVehicles,
      history,
      displayNotification,
      location: { search },
    } = this.props
    if (search !== prevProps.location.search) {
      const parsedQuery = convertStringToBoolean(parseQuery(search))
      fetchVehicles(parsedQuery)
    }
    redirectToSingleResult(
      currentVehicles,
      '/vehicles',
      history,
      displayNotification
    )
  }

  prevPageClick = () => {
    const {
      location: { search },
    } = this.props
    previousPage(search, this.props.history, '/vehicles')
  }

  nextPageClick = () => {
    const {
      currentVehicles,
      location: { search },
    } = this.props
    nextPage(search, this.props.history, currentVehicles.length, '/vehicles')
  }

  onPageClick = (pageNum) => {
    const {
      location: { search },
    } = this.props
    goToPage(search, this.props.history, pageNum, '/vehicles')
  }

  filtersClick = () => {
    const {
      location: { search },
    } = this.props
    this.props.history.push({
      pathname: '/vehicles/filters',
      search,
    })
  }

  countFilters = (query, defaultFilters) => {
    let count = 0

    forOwn(omit(query, defaultFilters), (obj) => {
      if (Array.isArray(obj)) {
        count += (obj as any[]).length
      } else {
        count += 1
      }
    })

    return count
  }

  filterBadge = () => {
    const {
      location: { search },
    } = this.props
    const parsedQuery = parseQuery(search)
    const defaultFilters = ['offset', 'limit']
    const count = this.countFilters(parsedQuery, defaultFilters)

    if (count > 0 && count < 10) {
      return <span className="index-table__filter-badge">{count}</span>
    } else if (count >= 10) {
      return (
        <span className="index-table__filter-badge index-table__filter-badge--multi">
          {count}
        </span>
      )
    } else {
      return <span className="index-table__filter-icon icon-filters" />
    }
  }

  openVehicle = (vehicleId: string) => {
    window.location.href = `/vehicles/${vehicleId}`
  }

  render() {
    const { currentVehicles, meta, location } = this.props
    const { total } = meta
    const { search } = location
    const parsedSearch = parseQuery(search)
    const totalPages = Math.ceil(total / parsedSearch.limit)
    let rows: ReactNode = null
    rows = currentVehicles.map((vehicle, index) => {
      const {
        id,
        plateState,
        year,
        make,
        model,
        financeable,
        latestOwnershipVerification,
      } = vehicle
      return (
        <tr
          className="index-table__row"
          key={index}
          onClick={() => this.openVehicle(id)}
        >
          {latestOwnershipVerification ? (
            <td className="index-table__cell">{`${latestOwnershipVerification.user.lastName}, ${latestOwnershipVerification.user.firstName}`}</td>
          ) : (
            <td />
          )}
          {plateState ? (
            <td className="index-table__cell">{plateState}</td>
          ) : (
            <td />
          )}
          {year ? (
            <td className="index-table__cell">{`${year} ${make} ${model}`}</td>
          ) : (
            <td />
          )}
          {financeable ? (
            <td className="index-table__cell">Yes</td>
          ) : (
            <td className="index-table__cell">No</td>
          )}
          {latestOwnershipVerification && latestOwnershipVerification.status ? (
            <td className="index-table__cell">
              {latestOwnershipVerification.status}
            </td>
          ) : (
            <td />
          )}
        </tr>
      )
    })
    return (
      <div className="index" key={location.key}>
        <h1 className="index-table__header">
          Vehicles Index
          <span className="index-table__header--total">{total}</span>
        </h1>
        <a
          data-testid="FiltersLink"
          onClick={this.filtersClick}
          className="index-table__filter-button"
        >
          {this.filterBadge()}
          filters
        </a>
        <table className="index-table">
          <thead>
            <tr className="index-table__row--head">
              <th className="index-table__cell">OWNER</th>
              <th className="index-table__cell">STATE</th>
              <th className="index-table__cell">VEHICLE</th>
              <th className="index-table__cell">FINANCEABLE</th>
              <th className="index-table__cell">OWNERSHIP</th>
            </tr>
          </thead>
          <tbody className="index-table__body">{rows}</tbody>
        </table>
        <TablePagination
          search={location.search}
          queryLength={currentVehicles.length}
          totalPages={totalPages}
          prevPageClick={this.prevPageClick}
          onPageClick={this.onPageClick}
          nextPageClick={this.nextPageClick}
        />
      </div>
    )
  }
}

export const mapStateToProps = (state) => {
  return {
    currentVehicles: selectVehicleIndex(state.vehicles),
    meta: selectVehicleMeta(state.vehicles),
  }
}

export default withRouter(
  connect(mapStateToProps, {
    fetchVehicles,
    fetchMakes,
    displayNotification,
  })(VehiclesIndex)
)
